import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus } from "lucide-react";
import { es } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { API } from "@/config";
import ServicePicker, { itemsTotal } from "@/components/site/ServicePicker";

const toKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const inputCls =
  "w-full rounded-xl border border-sand bg-cream px-4 py-2.5 text-sm text-obsidian placeholder:text-taupe/60 focus:outline-none focus:ring-2 focus:ring-gold";

export default function NewAppointmentDialog({ open, onClose, onCreated, headers }) {
  const [services, setServices] = useState([]);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", email: "", price: "", location: "estudio", address: "" });
  const [date, setDate] = useState(null);
  const [avail, setAvail] = useState(null);
  const [time, setTime] = useState("");
  const [customTime, setCustomTime] = useState("");
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [picked, setPicked] = useState(null);

  useEffect(() => {
    if (open) {
      setPicked(null);
      setItems([]);
      axios.get(`${API}/admin/clients`, { headers }).then((r) => setClients(r.data.clients)).catch(() => {});
    }
  }, [open]);

  const norm = (t) => (t || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const query = norm(form.name.trim());
  const digits = form.phone.replace(/\D/g, "");
  const suggestions = picked
    ? []
    : clients
        .filter((c) => (query.length >= 2 && norm(c.name).includes(query)) || (digits.length >= 3 && c.phone.replace(/\D/g, "").includes(digits)))
        .slice(0, 5);
  const pickClient = (c) => {
    setForm((f) => ({ ...f, name: c.name, phone: c.phone, email: c.email || "" }));
    setPicked(c);
  };

  useEffect(() => {
    if (open) {
      setForm({ name: "", phone: "", email: "", price: "", location: "estudio", address: "" });
      setDate(null);
      setTime("");
      setCustomTime("");
      setAvail(null);
      axios.get(`${API}/services`).then((r) => setServices(r.data)).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (date) {
      setTime("");
      setAvail(null);
      axios.get(`${API}/appointments/availability?date=${toKey(date)}`).then((r) => setAvail(r.data)).catch(() => {});
    }
  }, [date]);

  const changeItems = (next) => {
    setItems(next);
    setForm((f) => ({ ...f, price: next.length ? String(itemsTotal(services, next)) : "" }));
  };
  const finalTime = customTime || time;
  const ready = form.name.trim() && form.phone.trim() && items.length > 0 && date && /^\d{2}:\d{2}$/.test(finalTime) && (form.location === "estudio" || form.address.trim().length >= 5);

  const submit = async () => {
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/admin/appointments`,
        { ...form, items, price: form.price === "" ? null : Number(form.price), date: toKey(date), time: finalTime },
        { headers }
      );
      toast.success(form.email ? "Cita creada · email enviado a la clienta" : "Cita creada");
      onCreated(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo crear la cita");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="new-appointment-dialog" data-lenis-prevent className="bg-creamcard border-sand max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-obsidian">Nueva cita para una clienta</DialogTitle>
          <DialogDescription className="text-taupe">Se guardará como confirmada. Si pones su correo, le llegará la confirmación y el recordatorio.</DialogDescription>
        </DialogHeader>

        <div className="grid sm:grid-cols-2 gap-3">
          <input value={form.name} onChange={(e) => { setForm({ ...form, name: e.target.value }); setPicked(null); }} placeholder="Nombre de la clienta" data-testid="new-appt-name" className={inputCls} />
          <input value={form.phone} onChange={(e) => { setForm({ ...form, phone: e.target.value }); setPicked(null); }} placeholder="Teléfono" data-testid="new-appt-phone" className={inputCls} />
          {suggestions.length > 0 && (
            <div data-testid="new-appt-suggestions" className="sm:col-span-2 rounded-xl border border-gold/40 bg-cream divide-y divide-sand">
              {suggestions.map((c) => (
                <button key={c.key} type="button" onClick={() => pickClient(c)} data-testid={`new-appt-suggestion-${c.key}`} className="w-full text-left px-4 py-2 text-sm hover:bg-rosesoft transition-colors flex justify-between gap-3">
                  <span className="text-obsidian font-medium">{c.name}</span>
                  <span className="text-taupe text-xs">{c.phone} · {c.visits} visita{c.visits === 1 ? "" : "s"}</span>
                </button>
              ))}
            </div>
          )}
          {picked && (
            <div data-testid="new-appt-picked" className="sm:col-span-2 rounded-xl bg-amber-50 border border-amber-200 px-4 py-2.5 text-xs text-amber-900">
              <b>{picked.name}</b> · {picked.visits} visita{picked.visits === 1 ? "" : "s"}{picked.no_shows ? ` · ${picked.no_shows} falta${picked.no_shows > 1 ? "s" : ""}` : ""}
              {picked.notes ? <span className="block mt-1 whitespace-pre-line">📝 {picked.notes}</span> : <span className="block mt-1 text-amber-700/70">Sin notas</span>}
            </div>
          )}
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Correo (opcional)" data-testid="new-appt-email" className={`${inputCls} sm:col-span-2`} />
        </div>

        <div>
          <p className="text-xs tracking-[0.25em] uppercase text-gold font-medium">Servicios</p>
          <ServicePicker services={services} items={items} onChange={changeItems} testPrefix="new-appt" />
          <label className="flex items-center gap-2 mt-3 text-sm text-cocoa">
            Importe previsto
            <input type="number" min="0" step="0.5" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="new-appt-price" className="w-24 rounded-xl border border-sand bg-cream px-3 py-1.5 text-sm" />
            €
          </label>
        </div>

        <div>
          <p className="text-xs tracking-[0.25em] uppercase text-gold font-medium">¿Dónde?</p>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {[{ id: "estudio", label: "En mi estudio" }, { id: "domicilio", label: "En su domicilio" }].map((o) => (
              <button
                key={o.id}
                onClick={() => setForm({ ...form, location: o.id })}
                data-testid={`new-appt-location-${o.id}`}
                className={`rounded-xl border px-3 py-2 text-sm transition-colors ${
                  form.location === o.id ? "border-gold bg-rosesoft text-obsidian" : "border-sand bg-cream text-cocoa hover:border-gold/50"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
          {form.location === "domicilio" && (
            <textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Dirección completa de la clienta" rows={2} data-testid="new-appt-address" className={`${inputCls} mt-2`} />
          )}
        </div>

        <div>
          <p className="text-xs tracking-[0.25em] uppercase text-gold font-medium">Día y hora</p>
          <div className="mt-2 flex justify-center rounded-xl border border-sand bg-cream">
            <Calendar mode="single" locale={es} selected={date} onSelect={setDate} data-testid="new-appt-calendar" />
          </div>
          {date && (
            <div className="mt-3">
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                {(avail?.slots || []).map((s) => {
                  const busy = avail.taken.includes(s);
                  return (
                    <button
                      key={s}
                      disabled={busy}
                      onClick={() => { setTime(s); setCustomTime(""); }}
                      data-testid={`new-appt-slot-${s.replace(":", "")}`}
                      className={`rounded-lg border px-2 py-1.5 text-sm transition-colors ${
                        busy ? "border-sand bg-sand/40 text-taupe/50 line-through cursor-not-allowed" : time === s && !customTime ? "border-gold bg-gold text-white" : "border-sand bg-cream text-cocoa hover:border-gold/60"
                      }`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
              <label className="flex items-center gap-2 mt-3 text-sm text-cocoa">
                U otra hora:
                <input type="time" value={customTime} onChange={(e) => setCustomTime(e.target.value)} data-testid="new-appt-custom-time" className="rounded-xl border border-sand bg-cream px-3 py-1.5 text-sm" />
              </label>
            </div>
          )}
        </div>

        <button
          onClick={submit}
          disabled={!ready || saving}
          data-testid="new-appt-submit"
          className="w-full rounded-xl bg-gold hover:bg-gold-dark disabled:opacity-50 text-white text-sm font-medium py-3 transition-colors flex items-center justify-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Guardar cita
        </button>
      </DialogContent>
    </Dialog>
  );
}
